// Aura Score card on the Result page. Shows CTA → navigates to /aura page,
// or renders the cached result inline once it's been computed.
import { Sparkles, Wand2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AuraScoreRing } from "./AuraScoreRing";
import { cn } from "@/lib/utils";

export interface AuraData {
  aura_score: number | null;
  aura_silhouette: number | null;
  aura_proportions: number | null;
  aura_color_season: number | null;
  aura_statement: number | null;
  aura_photo_readiness: number | null;
  aura_verdict: string | null;
  aura_tips: string[] | null;
}

interface Props {
  tryonId: string;
  data: AuraData;
}

const SUB_LABELS: Array<[keyof AuraData, string]> = [
  ["aura_silhouette", "Silhouette"],
  ["aura_proportions", "Proportions"],
  ["aura_color_season", "Color season"],
  ["aura_statement", "Statement factor"],
  ["aura_photo_readiness", "Photo-readiness"],
];

export const AuraScoreCard = ({ tryonId, data }: Props) => {
  const navigate = useNavigate();
  const hasResult = typeof data.aura_score === "number";

  if (!hasResult) {
    return (
      <div className="mt-4 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-4 shadow-soft">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
            <Wand2 className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold">Aura Score</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Get a deeper read on your vibe, silhouette and signature style.
              <span className="ml-1 font-medium text-foreground">Costs 0.10 credits.</span>
            </p>
          </div>
        </div>
        <Button
          onClick={() => navigate(`/app/result/${tryonId}/aura`)}
          className="mt-3 h-11 w-full gradient-primary text-primary-foreground"
        >
          <Sparkles className="mr-2 h-4 w-4" />
          Get Aura Score
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-5 shadow-soft animate-fade-in">
      <div className="flex flex-col items-center text-center">
        <AuraScoreRing score={data.aura_score!} size={128} />
        {data.aura_verdict && (
          <p className="mt-3 max-w-xs text-sm leading-relaxed text-foreground/90">
            "{data.aura_verdict}"
          </p>
        )}
      </div>

      <div className="mt-5 space-y-2.5">
        {SUB_LABELS.map(([key, label]) => {
          const v = data[key] as number | null;
          if (typeof v !== "number") return null;
          return (
            <div key={key}>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-semibold tabular-nums">{v}</span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={cn(
                    "h-full rounded-full bg-gradient-to-r from-primary to-primary/60 transition-all",
                  )}
                  style={{ width: `${Math.max(0, Math.min(100, v))}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {Array.isArray(data.aura_tips) && data.aura_tips.length > 0 && (
        <div className="mt-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-primary">
            Aura Tips
          </p>
          <ul className="space-y-2">
            {data.aura_tips.map((t, i) => (
              <li key={i} className="flex gap-2 text-sm">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
